import { useState, useRef, useEffect, KeyboardEvent } from 'react';

interface EditableFieldProps {
  /** 표시할 값 */
  value: string;
  /** 저장 핸들러 */
  onSave: (value: string) => Promise<void>;
  /** 편집 타입 */
  type?: 'text' | 'textarea' | 'select';
  /** select용 옵션 */
  options?: { label: string; value: string }[];
  /** 읽기 전용 */
  disabled?: boolean;
  /** 플레이스홀더 */
  placeholder?: string;
  /** 커스텀 렌더러 (값을 표시할 때 사용) */
  renderValue?: (value: string) => React.ReactNode;
  /** 추가 CSS 클래스 */
  className?: string;
  /** 수정 중일 때의 추가 CSS 클래스 */
  editClassName?: string;
  /** 에러 메시지 */
  error?: string;
}

export function EditableField({
  value,
  onSave,
  type = 'text',
  options,
  disabled = false,
  placeholder = '클릭하여 편집',
  renderValue,
  className = '',
  editClassName = '',
  error: externalError,
}: EditableFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);

  // 외부 값 변경 시 내부 상태 동기화
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  // 편집 모드 진입 시 포커스
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // text/textarea의 경우 커서를 끝으로
      if (type !== 'select' && inputRef.current instanceof HTMLInputElement) {
        inputRef.current.setSelectionRange(
          inputRef.current.value.length,
          inputRef.current.value.length
        );
      }
    }
  }, [isEditing, type]);

  const handleEdit = () => {
    if (disabled) return;
    setEditValue(value);
    setIsEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value);
    setError(null);
  };

  const handleSave = async () => {
    // 변경 없으면 취소
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      await onSave(editValue);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : '저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter' && type !== 'textarea') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Enter' && e.ctrlKey && type === 'textarea') {
      e.preventDefault();
      handleSave();
    }
  };

  // 편집 모드
  if (isEditing) {
    return (
      <div className={`relative ${editClassName}`}>
        {type === 'textarea' ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-sm border rounded-control focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none ${
              isSaving ? 'opacity-50' : ''
            } ${error ? 'border-red-300' : 'border-ink-300'}`}
            rows={3}
          />
        ) : type === 'select' ? (
          <select
            ref={inputRef as React.RefObject<HTMLSelectElement>}
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value);
              // select는 변경 시 바로 저장
              onSave(e.target.value)
                .then(() => setIsEditing(false))
                .catch((err) =>
                  setError(err instanceof Error ? err.message : '저장에 실패했습니다.')
                );
            }}
            onBlur={handleCancel}
            disabled={isSaving}
            className={`px-2 py-1 text-sm border rounded-control focus:outline-none focus:ring-2 focus:ring-brand-500 ${
              isSaving ? 'opacity-50' : ''
            } ${error ? 'border-red-300' : 'border-ink-300'}`}
          >
            {options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-sm border rounded-control focus:outline-none focus:ring-2 focus:ring-brand-500 ${
              isSaving ? 'opacity-50' : ''
            } ${error ? 'border-red-300' : 'border-ink-300'}`}
          />
        )}

        {/* 에러 메시지 */}
        {(error || externalError) && (
          <div className="absolute -bottom-5 left-0 text-xs text-red-500">
            {error || externalError}
          </div>
        )}

        {/* 저장 중 표시 */}
        {isSaving && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <div className="animate-spin w-3 h-3 border-2 border-brand-500 border-t-transparent rounded-full" />
          </div>
        )}

        {/* 키보드 힌트 (textarea) */}
        {type === 'textarea' && !isSaving && (
          <div className="text-xs text-ink-400 mt-1">
            Ctrl+Enter로 저장, Esc로 취소
          </div>
        )}
      </div>
    );
  }

  // 읽기 모드
  const displayValue = value || placeholder;
  const isEmpty = !value;

  return (
    <span
      onClick={handleEdit}
      className={`
        group inline-flex items-center gap-1
        ${disabled ? 'cursor-default' : 'cursor-pointer hover:bg-surface-hover'}
        rounded px-1 -mx-1 transition-colors
        ${isEmpty ? 'text-ink-400 italic' : ''}
        ${className}
      `}
      title={disabled ? undefined : '클릭하여 편집'}
    >
      {renderValue ? renderValue(value) : displayValue}
    </span>
  );
}

export default EditableField;
